import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BlahblahCard } from '@/components/BlahblahCard';
import * as blahblahsApi from '@/api/blahblahs';
import type { Blahblah } from '@/types';

export function BlahblahDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [blahblah, setBlahblah] = useState<Blahblah | null>(null);
  const [replies, setReplies] = useState<Blahblah[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadBlahblah = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [blahblahData, repliesData] = await Promise.all([
          blahblahsApi.getBlahblah(id),
          blahblahsApi.getReplies(id),
        ]);

        setBlahblah(blahblahData);
        setReplies(repliesData.items);
        setNextCursor(repliesData.nextCursor);
        setHasMore(repliesData.hasMore);

        // Record view
        blahblahsApi.recordView(id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load blahblah';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadBlahblah();
  }, [id]);

  const handleLoadMore = async () => {
    if (!id || !hasMore || isLoadingMore || !nextCursor) return;

    setIsLoadingMore(true);

    try {
      const response = await blahblahsApi.getReplies(id, nextCursor);
      setReplies((prev) => [...prev, ...response.items]);
      setNextCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch {
      // Silently fail for load more
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !isLoadingMore) {
      handleLoadMore();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error || !blahblah) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-red-500 mb-4">{error || 'Blahblah not found'}</p>
        <Link to="/" className="text-indigo-600 hover:underline">
          Go back to feed
        </Link>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7.5rem)] overflow-y-auto" onScroll={handleScroll}>
      {/* Main blahblah */}
      <BlahblahCard blahblah={blahblah} />

      {/* Full transcript */}
      {blahblah.transcript && (
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 mb-2">Transcript</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{blahblah.transcript}</p>
        </div>
      )}

      {/* Replies section */}
      <div className="border-t border-gray-200">
        <div className="p-4 flex items-center justify-between">
          <h2 className="text-sm font-medium text-gray-500">
            Replies ({blahblah.replyCount})
          </h2>
          <button
            onClick={() => navigate('/record', { state: { parentId: blahblah.id, parentBlahblah: blahblah } })}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <MicIcon className="w-4 h-4" />
            Record a Reply
          </button>
        </div>

        {replies.length === 0 ? (
          <div className="text-center py-8 px-4">
            <p className="text-gray-500 text-sm">No replies yet</p>
          </div>
        ) : (
          <div>
            {replies.map((reply) => (
              <div key={reply.id} className="border-l-2 border-indigo-200 ml-4">
                <BlahblahCard blahblah={reply} />
              </div>
            ))}
          </div>
        )}

        {/* Loading more indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
          </div>
        )}
      </div>
    </div>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" strokeLinecap="round" />
      <line x1="8" y1="23" x2="16" y2="23" strokeLinecap="round" />
    </svg>
  );
}
